import "@whatslly/ui-components/Pages/CreateSupportCaseForm/i18n/config";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import classes from "./CreateSupportCaseForm.module.scss";
import { useTranslation } from "react-i18next";
import { UploadFilesV2 } from "@whatslly/ui-components/v2/UploadFiles";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { observer } from "mobx-react-lite";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import type { SelectOption } from "@whatslly/types/UI-V2/BaseElements/SelectType";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { FormTextArea } from "@whatslly/ui-components/v2/FormElements/FormTextArea";
import isEmail from "validator/es/lib/isEmail";
import { MultipleInputSelect, MultipleInputSelectRef } from "@whatslly/ui-components/MultipleInputSelect";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import classnames from "classnames";
import { Field, FieldProps, FormikProps } from "formik";

export type StateType = {
  subject: string;
  emails: string;
  description: string;
  businessImpact: SelectOption<{ label: string; value: string }>;
  files: File[];
};

export const CreateSupportCaseForm: React.FC<{
  onSubmit: (stateFields: StateType) => void;
  setAllRequiredFieldsExists: React.Dispatch<React.SetStateAction<boolean>>;
  formRef?: React.MutableRefObject<FormikProps<StateType> | null>;
  className?: string;
  menuRef?: React.MutableRefObject<MultipleInputSelectRef | null>;
}> = observer(({ onSubmit, setAllRequiredFieldsExists, formRef, menuRef, className }) => {
  const focusElementNumberFunctionRef = useRef<((elementNumber: number) => void) | null>(null);
  const { t } = useTranslation();

  const initialFormState = useMemo(
    () => ({
      emails: "",
      subject: "",
      description: "",
      businessImpact: { label: t("support_case.medium"), value: `Medium` },
      files: [] as File[],
    }),
    [t]
  );

  const onValidate = useCallback(
    (stateFields: StateType) => {
      const valid: boolean[] = [];
      for (const field in stateFields) {
        if (field === "businessImpact" && typeof stateFields[field] === "object") {
          valid.push(!!stateFields.businessImpact?.value);
        }
        if (field === "emails") {
          const emails = stateFields.emails
            .split(",")
            .map((item: string) => item.trim())
            .filter(Boolean);
          valid.push(emails.length > 0 && emails.every((email) => isEmail(email)));
        }

        if (field === "subject" || (field === "description" && typeof stateFields[field] === "string")) {
          valid.push(stateFields[field].length > 0);
        }
      }
      const allRequiredFieldsExists = valid.every(Boolean) && valid.length === 4;
      setAllRequiredFieldsExists(allRequiredFieldsExists);
    },
    [setAllRequiredFieldsExists]
  );

  const businessImpactOptions = useMemo(
    () =>
      [
        { label: t("support_case.critical"), value: `Critical` },
        { label: t("support_case.high"), value: `High` },
        { label: t("support_case.medium"), value: `Medium` },
        { label: t("support_case.low"), value: `Low` },
      ] as SelectOption<{ label: string; value: string }>[],
    [t]
  );

  useEffect(() => {
    if (focusElementNumberFunctionRef.current) {
      focusElementNumberFunctionRef.current(0);
    }
  }, []);

  return (
    <div className={classnames(classes.wrapper, className)}>
      <div className={classes.infoContainer}>
        <SvgV2 iconName={`info`} width={18} height={18} />
        <span>
          {t("support_case.info1")} {t("support_case.info2")}{" "}
        </span>
      </div>
      <div className={classnames(classes.formContainer)}>
        <FormElement
          formikRef={formRef}
          onSubmit={(values) => onSubmit(values)}
          initialValues={initialFormState}
          focusElementNumberFunctionRef={focusElementNumberFunctionRef}
          validate={(values) => onValidate(values)}
        >
          <div className={classes.formElements}>
            <Field
              name={`emails`}
              children={(fieldData: FieldProps) => {
                return (
                  <MultipleInputSelect
                    ref={menuRef}
                    required={true}
                    labelText={t("support_case.emails")}
                    prefix={`emails`}
                    testId={`support_case_emails`}
                    items={
                      fieldData.field.value
                        .split(",")
                        .map((item: string) => item.trim())
                        .filter(Boolean) ?? []
                    }
                    onChange={(items) => {
                      fieldData.form.setFieldValue(fieldData.field.name, items.join(","));
                    }}
                    isValidFn={isEmail}
                    itemType={`email`}
                    showError={true}
                  />
                );
              }}
            />

            <FormTextInput
              marginTop={`extra-small`}
              removeClear
              required={true}
              labelText={t("support_case.subject")}
              name={`subject`}
              data-testid={`form-subject`}
              className={classes.textInput}
              type="text"
              paddingInline={`none`}
            />
            <Field
              name={`businessImpact`}
              children={(fieldData: FieldProps) => {
                return (
                  <FormSelect<{ label: string; value: string }>
                    showClear
                    required={true}
                    data-testid={`form-businessImpact`}
                    options={businessImpactOptions}
                    name={`businessImpact`}
                    labelText={t("support_case.businessImpact")}
                    paddingInline={`none`}
                    defaultValue={initialFormState.businessImpact}
                    value={fieldData.field.value}
                  />
                );
              }}
            />

            <FormTextArea
              required={true}
              labelText={t("support_case.description")}
              name={`description`}
              data-testid={`form-description`}
              className={classes.textArea}
              type="text"
              paddingInline={`none`}
            />
            <Field
              name={`files`}
              children={(fieldData: FieldProps) => {
                return (
                  <UploadFilesV2
                    files={fieldData.field.value}
                    className={classes.uploadFiles}
                    showClear={true}
                    onClear={(fileName) => {
                      const filesToSet = fieldData.field.value.filter((file: File) => file.name !== fileName);
                      fieldData.form.setFieldValue(fieldData.field.name, filesToSet);
                      return;
                    }}
                    onChange={(files) => {
                      const filesAsArray = Array.from(files);
                      fieldData.form.setFieldValue(fieldData.field.name, [...fieldData.field.value, ...filesAsArray]);
                    }}
                  />
                );
              }}
            />
          </div>
        </FormElement>
      </div>
    </div>
  );
});
